import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Star, MoreVertical, Minus, Plus, RotateCcw, Eye, EyeOff, Type, Maximize2, Pencil, FolderPlus, FileText, ExternalLink, Music4, Loader2, Save, X, Share2, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import ChordSheetViewer from "@/components/ChordSheetViewer";
import StageLite from "@/components/StageLite";
import { fileUrl } from "@/components/FileViewer";
import { sanitizeChordText, transposeKey, preferFlatsFor } from "@/lib/chords";

const KIND_LABEL = { image: "Imagem", pdf: "PDF", word: "Word" };

export default function SongView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stage, setStage] = useState(false);

  // display controls (inline, no modal)
  const [steps, setSteps] = useState(0);
  const [hideChords, setHideChords] = useState(false);
  const [fontPx, setFontPx] = useState(15);
  const [showFont, setShowFont] = useState(false);

  // inline edit
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // setlist modal
  const [setlistOpen, setSetlistOpen] = useState(false);
  const [setlists, setSetlists] = useState([]);
  const [checked, setChecked] = useState({});
  const [newName, setNewName] = useState("");
  const [savingSet, setSavingSet] = useState(false);
  const [sharing, setSharing] = useState(false);

  const load = () => api.get(`/songs/${id}`).then((r) => setSong(r.data)).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [id]);

  // Navegação entre músicas (contexto vindo da Biblioteca ou Setlist)
  const location = useLocation();
  const nav = location.state?.nav;
  const navIds = Array.isArray(nav?.ids) ? nav.ids : [];
  const navIdx = navIds.indexOf(id);
  const hasNav = navIdx >= 0 && navIds.length > 1;
  const goToSong = (sid) => { if (sid && sid !== id) navigate(`/repertorio/musica/${sid}`, { state: { nav } }); };
  const goDelta = (d) => { const ni = navIdx + d; if (!hasNav || ni < 0 || ni >= navIds.length) return; goToSong(navIds[ni]); };
  const touchRef = useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => { const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    if (!hasNav || editMode) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x, dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) goDelta(dx < 0 ? 1 : -1);
  };
  useEffect(() => {
    if (!hasNav) return;
    const onKey = (e) => {
      if (editMode) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      if (e.key === "ArrowRight") { e.preventDefault(); goDelta(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goDelta(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasNav, navIdx, editMode, navIds]); // eslint-disable-line

  const structured = song?.structured && Array.isArray(song.structured.sections) && song.structured.sections.length ? song.structured : null;
  const isFile = !!song?.file_id;
  const baseKey = structured?.original_key || song?.key || "";
  const curKey = transposeKey(baseKey, steps, preferFlatsFor(transposeKey(baseKey, steps, false)));

  const toggleFavorite = async () => {
    const next = !song.favorite;
    setSong((s) => ({ ...s, favorite: next }));
    try { await api.patch(`/songs/${id}/favorite`, { favorite: next }); toast.success(next ? "Adicionada à biblioteca!" : "Removida da biblioteca."); }
    catch (e) { setSong((s) => ({ ...s, favorite: !next })); toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = () => { setEditText(song.lyrics || ""); setSetlistOpen(false); setEditMode(true); };
  const saveEdit = async () => {
    if (!editText.trim()) return toast.error("Cole a letra/cifra da música.");
    setSavingEdit(true);
    try {
      const { data: fmt } = await api.post("/songs/format", { text: editText, title: song.title, artist: song.artist || "", key: song.key || "", use_ai: true, source: "user_content" });
      const structuredNew = { original_key: fmt.original_key || song.key || "", current_key: fmt.original_key || song.key || "", sections: fmt.sections || [], source: song.structured?.source || "web" };
      await api.put(`/songs/${id}`, { title: song.title, artist: song.artist || "", key: song.key || "", lyrics: editText, chords: "", notes: song.notes || "", category: song.category || "", tags: song.tags || [], structured: structuredNew, favorite: !!song.favorite });
      toast.success("Cifra atualizada!");
      setEditMode(false); setSteps(0); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const openSetlist = async () => {
    setEditMode(false); setChecked({}); setNewName(""); setSetlistOpen(true);
    try { const { data } = await api.get("/repertoires"); setSetlists(data || []); } catch { setSetlists([]); }
  };
  const strip = () => ({ title: song.title, artist: song.artist || "", key: song.key || "", lyrics: song.lyrics || "", notes: song.notes || "", song_id: song.id, structured: song.structured, file_id: song.file_id, file_kind: song.file_kind });
  const saveSetlist = async () => {
    const ids = Object.keys(checked).filter((k) => checked[k]);
    if (!ids.length && !newName.trim()) return toast.error("Escolha um setlist ou crie um novo.");
    setSavingSet(true);
    try {
      for (const rid of ids) await api.post(`/repertoires/${rid}/songs`, strip());
      if (newName.trim()) await api.post("/repertoires", { name: newName.trim(), songs: [strip()] });
      toast.success("Adicionada ao setlist!");
      setSetlistOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingSet(false); }
  };

  const sharePdf = async () => {
    if (!structured) return toast.error("Sem cifra para exportar.");
    setSharing(true);
    try {
      const res = await api.post("/songs/sheet.pdf", { title: song.title || "", artist: song.artist || "", structured }, { responseType: "blob" });
      const fname = `${(song.title || "cifra").replace(/\s+/g, "_").slice(0, 40) || "cifra"}.pdf`;
      const file = new File([res.data], fname, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: song.title || "Cifra" });
      else { const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = fname; a.click(); URL.revokeObjectURL(url); toast.success("PDF baixado!"); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível gerar o PDF."); }
    finally { setSharing(false); }
  };

  if (notFound) return (
    <div className="max-w-lg mx-auto text-center py-20" data-testid="song-view-notfound">
      <Music4 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
      <h1 className="font-head text-xl font-bold">Música não encontrada</h1>
      <Button onClick={() => navigate("/repertorio")} className="mt-5 rounded-md gap-2"><ArrowLeft className="h-4 w-4" />Voltar ao repertório</Button>
    </div>
  );
  if (!song) return <div className="text-muted-foreground py-10 flex items-center gap-2" data-testid="song-view-loading"><Loader2 className="h-4 w-4 animate-spin" />Carregando...</div>;

  const iconBtn = "h-10 w-10 grid place-items-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

  return (
    <div className="max-w-5xl xl:max-w-6xl mx-auto pb-10" data-testid="song-view-page" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* ===== Barra única de ferramentas (topo) ===== */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-2.5 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between gap-2">
          {/* Transpor (esquerda) */}
          <div className="flex items-center gap-1.5 rounded-xl border border-border bg-surface px-1.5 py-1" data-testid="sv-transpose">
            <button data-testid="sv-transpose-down" onClick={() => setSteps((s) => s - 1)} aria-label="Descer meio tom" className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover"><Minus className="h-4 w-4" /></button>
            <div className="text-center leading-none px-1">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Tom</div>
              <div className="font-mono font-bold text-accent text-sm" data-testid="sv-current-key">{curKey || "—"}</div>
            </div>
            <button data-testid="sv-transpose-up" onClick={() => setSteps((s) => s + 1)} aria-label="Subir meio tom" className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover"><Plus className="h-4 w-4" /></button>
            {steps !== 0 && <button data-testid="sv-transpose-reset" onClick={() => setSteps(0)} aria-label="Tom original" className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-accent"><RotateCcw className="h-4 w-4" /></button>}
          </div>
          {/* Ícones (direita) */}
          <div className="flex items-center gap-1.5">
            <button data-testid="sv-toggle-chords" onClick={() => setHideChords((v) => !v)} aria-label={hideChords ? "Mostrar cifras" : "Só letra (cantores)"} aria-pressed={hideChords} title={hideChords ? "Mostrar cifras" : "Só letra (cantores)"}
              className={`${iconBtn} ${hideChords ? "bg-accent/15 border-accent/40 text-accent" : "bg-surface border-border text-muted-foreground hover:text-foreground"}`}>{hideChords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
            <button data-testid="sv-font-toggle" onClick={() => setShowFont((v) => !v)} aria-label="Tamanho da fonte" aria-pressed={showFont} title="Tamanho da fonte"
              className={`${iconBtn} ${showFont ? "bg-accent/15 border-accent/40 text-accent" : "bg-surface border-border text-muted-foreground hover:text-foreground"}`}><Type className="h-5 w-5" /></button>
            <button data-testid="cs-stage-mode-btn" onClick={() => setStage(true)} aria-label="Modo Palco" title="Modo Palco"
              className={`${iconBtn} bg-primary border-primary text-primary-foreground hover:bg-primary/90`}><Maximize2 className="h-5 w-5" /></button>
          </div>
        </div>
        {/* Slider de fonte inline */}
        {showFont && (
          <div className="mt-2 flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2" data-testid="sv-font-bar">
            <Type className="h-4 w-4 text-muted-foreground shrink-0" />
            <button data-testid="sv-font-dec" onClick={() => setFontPx((f) => Math.max(12, f - 1))} aria-label="Diminuir fonte" className="h-8 w-8 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-foreground"><Minus className="h-4 w-4" /></button>
            <input data-testid="sv-font-slider" type="range" min="12" max="28" value={fontPx} onChange={(e) => setFontPx(parseInt(e.target.value))} className="flex-1 h-2 accent-accent" aria-label="Ajustar tamanho da fonte" />
            <button data-testid="sv-font-inc" onClick={() => setFontPx((f) => Math.min(28, f + 1))} aria-label="Aumentar fonte" className="h-8 w-8 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-foreground"><Plus className="h-4 w-4" /></button>
            <span className="font-mono text-sm text-foreground w-10 text-right" data-testid="sv-font-val">{fontPx}px</span>
          </div>
        )}
      </div>

      {/* ===== Voltar + Favorito + Menu ===== */}
      <div className="flex items-center justify-between gap-2 mt-4">
        <Button data-testid="sv-back" onClick={() => navigate("/repertorio")} className="rounded-lg gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"><ArrowLeft className="h-4 w-4" />Voltar</Button>
        <div className="flex items-center gap-1.5">
          <button data-testid="cs-library-heart-btn" onClick={toggleFavorite} aria-label={song.favorite ? "Remover da biblioteca" : "Adicionar à biblioteca"} aria-pressed={!!song.favorite} title={song.favorite ? "Na biblioteca" : "Adicionar à biblioteca"}
            className={`${iconBtn} ${song.favorite ? "bg-accent/15 border-accent/40 text-accent" : "bg-surface border-border text-muted-foreground hover:text-foreground"}`}><Star className={`h-5 w-5 ${song.favorite ? "fill-current" : ""}`} /></button>
          <button data-testid="cs-add-setlist-btn" onClick={openSetlist} aria-label="Adicionar a setlist" title="Adicionar a setlist" className={`${iconBtn} bg-surface border-border text-muted-foreground hover:text-foreground`}><FolderPlus className="h-5 w-5" /></button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="sv-menu-trigger" aria-label="Mais ações" className={`${iconBtn} bg-surface border-border text-muted-foreground hover:text-foreground`}><MoreVertical className="h-5 w-5" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem data-testid="cs-edit-btn" onClick={openEdit} disabled={isFile} className="gap-2"><Pencil className="h-4 w-4" />Editar cifra</DropdownMenuItem>
              <DropdownMenuItem data-testid="cs-export-pdf-btn" onClick={sharePdf} disabled={!structured || sharing} className="gap-2"><Share2 className="h-4 w-4" />Exportar PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ===== Título ===== */}
      <div className="mt-3">
        <h1 className="font-head text-2xl lg:text-3xl font-extrabold tracking-tight">{song.title}</h1>
        {song.artist && <p className="text-sm text-muted-foreground font-medium">{song.artist}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {baseKey && <span className="px-2.5 py-1 rounded-md border border-accent/40 bg-accent/10 text-accent font-mono" data-testid="sv-key-badge">Tom: {curKey || baseKey}</span>}
          {song.category && <span className="px-2.5 py-1 rounded-md border border-border bg-surface text-muted-foreground">{song.category}</span>}
          {song.favorite && <span className="px-2.5 py-1 rounded-md border border-accent/40 bg-accent/10 text-accent inline-flex items-center gap-1"><Star className="h-3 w-3 fill-current" />Biblioteca</span>}
        </div>
      </div>

      {/* ===== Paginação entre músicas ===== */}
      {hasNav && (
        <div className="mt-3 flex flex-col items-center gap-1" data-testid="sv-song-pagination">
          <div className="flex items-center gap-1.5 max-w-full overflow-x-auto no-scrollbar px-1 py-1">
            {navIds.map((sid, i) => (
              <button key={sid} data-testid={`sv-page-dot-${i}`} onClick={() => goToSong(sid)} aria-label={`Ir para música ${i + 1}`} aria-current={i === navIdx}
                className={`h-2.5 rounded-full shrink-0 transition-all ${i === navIdx ? "w-6 bg-accent" : "w-2.5 bg-muted-foreground/30 hover:bg-muted-foreground/60"}`} />
            ))}
          </div>
          <span className="text-[11px] text-muted-foreground" data-testid="sv-page-count">{navIdx + 1} / {navIds.length} · deslize ou use ← →</span>
        </div>
      )}

      {/* ===== Conteúdo ===== */}
      <div className="mt-4">
        {editMode ? (
          <div className="space-y-3" data-testid="sv-edit-inline">
            <p className="text-xs text-muted-foreground">Ajuste a letra/cifra (acordes acima das linhas). Ao salvar, reformatamos e atualizamos a estrutura.</p>
            <Textarea data-testid="sv-edit-text" value={editText} onChange={(e) => setEditText(e.target.value)} className="bg-background border-border rounded-md font-mono text-sm min-h-[55vh]" autoFocus />
            <div className="flex justify-end gap-2">
              <Button data-testid="sv-edit-cancel" variant="outline" onClick={() => setEditMode(false)} className="rounded-md bg-transparent">Cancelar</Button>
              <Button data-testid="sv-edit-save" onClick={saveEdit} disabled={savingEdit} className="rounded-md gap-2">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvar alterações</Button>
            </div>
          </div>
        ) : structured ? (
          <ChordSheetViewer structured={structured} music={song.title} artist={song.artist} showHeader={false} showNav={false} steps={steps} hideChords={hideChords} fontPx={fontPx} />
        ) : isFile ? (
          <div className="bg-surface border border-border rounded-xl p-6 text-center space-y-3" data-testid="sv-file">
            <FileText className="h-10 w-10 mx-auto text-info" />
            <p className="text-sm text-muted-foreground">Arquivo original · {KIND_LABEL[song.file_kind] || "Arquivo"}</p>
            {song.file_kind === "image" && <img src={fileUrl(song.file_id)} alt={song.title} className="max-h-[70vh] mx-auto rounded-md" />}
            {song.file_kind === "pdf" && <iframe title={song.title} src={fileUrl(song.file_id)} className="w-full h-[70vh] rounded-md bg-white" />}
            {song.file_kind === "word" && <a href={fileUrl(song.file_id)} target="_blank" rel="noreferrer"><Button className="rounded-md gap-2"><ExternalLink className="h-4 w-4" />Abrir documento</Button></a>}
          </div>
        ) : (
          <div className="bg-surface border border-border rounded-xl p-4" data-testid="sv-plain">
            <pre className="whitespace-pre-wrap font-mono text-foreground leading-relaxed" style={{ fontSize: `${fontPx}px` }}>{sanitizeChordText(song.lyrics) || "Sem cifra/letra cadastrada."}</pre>
          </div>
        )}
      </div>

      <StageLite open={stage} songs={[song]} onClose={() => setStage(false)} />

      {/* ===== Modal: Adicionar a setlist ===== */}
      <Dialog open={setlistOpen} onOpenChange={setSetlistOpen}>
        <DialogContent className="max-w-md" data-testid="cs-setlist-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderPlus className="h-5 w-5 text-accent" />Adicionar a setlist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {setlists.length > 0 ? (
              <div className="max-h-56 overflow-y-auto space-y-1.5">
                {setlists.map((g) => (
                  <label key={g.id} data-testid={`cs-setlist-item-${g.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface hover:border-accent/40 cursor-pointer transition-colors">
                    <Checkbox data-testid={`cs-setlist-check-${g.id}`} checked={!!checked[g.id]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [g.id]: !!v }))} />
                    <span className="min-w-0 flex-1"><span className="block font-medium text-sm truncate">{g.name}</span><span className="block text-xs text-muted-foreground">{(g.songs || []).length} música(s)</span></span>
                  </label>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Você ainda não tem setlists. Crie um abaixo.</p>}
            <div className="flex items-center gap-2 border-t border-border pt-3">
              <Plus className="h-4 w-4 text-primary shrink-0" />
              <Input data-testid="cs-create-setlist-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Criar novo setlist (ex: Pagode 90)" className="bg-background border-border rounded-md" />
            </div>
            <Button data-testid="cs-setlist-save-confirm" onClick={saveSetlist} disabled={savingSet} className="w-full rounded-md gap-2">{savingSet ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
